import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { List } from 'react-virtualized';
import { attributeStore } from "../../store/attributeStore";
import { NavigationService } from "../../services/NavigationService";
import ScrollHint from "../ScrollHint/ScrollHint";
import { IoIosArrowBack } from "react-icons/io";

import TableToolbar from './components/TableToolbar';
import TableHeader from './components/TableHeader';
import CategorySeparator from './components/CategorySeparator';
import TableRow from './components/TableRow';

import { useTableRows } from './hooks/useTableRows';
import { useTableHeight } from './hooks/useTableHeight';
import { useFilteredRows } from './hooks/useFilteredRows';
import { useTempValues } from './hooks/useTempValues';
import { useSubdivisionWidths } from './hooks/useSubdivisionWidths';
import { useTableNavigation } from './hooks/useTableNavigation';
import { useCellInteractions } from './hooks/useCellInteractions';

import { deepClone } from './utils/arrayUtils';
import { calculateColumnWidths, getLongestStringLength } from './utils/tableUtils';
import { createRowsWithSeparation } from './utils/rowUtils';
import {LogsServices} from "../../services/LogsServices";
import {DynamicTableService} from "../../services/DynamicTableService";
import {tableDimensionsStore} from "../../store/tableDimensionsStore";

const DynamicTable = ({
  title,
  columnsPreload,
  rowsPreload,
  onSave,
  cacheKey,
  splitMode = true,
  fixedNetworkAdjustmentFactors,
  stateGroupings,
  optional,
  hints,
  ignoreCache,
  attributeSeparationLines,
  isInstructionOpen,
  originalDataBeforeUpload,
  loaded,
  setLoaded
}) => {
  const rowsPreloadRef = useRef(rowsPreload);
  const [columns, setColumns] = useState(columnsPreload);
  const [changes, setChanges] = useState(false);
  const [steps, setSteps] = useState([]);
  const [wasFileUploaded, setWasFileUploaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [optionStatus, setOptionStatus] = useState('');
  const [tooltipVisible, setTooltipVisible] = useState(new Array(columns.length).fill(false));
  const [headerHeight, setHeaderHeight] = useState(stateGroupings ? 80 : 40);
  const [useCustomWidths, setUseCustomWidths] = useState(true);
  const [customWidths, setCustomWidths] = useState({});
  const [subdivisionWidth, setSubdivisionWidth] = useState({});
  const [rowHeights, setRowHeights] = useState({});
  const scrollableRef = useRef(null);
  const scrollableContainerRef = useRef(null);
  const headerRef = useRef(null);
  const listRef = useRef(null);
  const [mouseDown, setMouseDown] = useState(false);
  const dimensionsAppliedRef = useRef(false);

  const { rows, setRows } = useTableRows(rowsPreload, cacheKey, ignoreCache, setChanges, setWasFileUploaded);
  const maxHeight = useTableHeight(isInstructionOpen);
  const filteredRows = useFilteredRows(rows, searchTerm, optionStatus);
  const { tempValues, updateTempValue, clearTempValue } = useTempValues();
  const {
    subdivisionRefs,
    resizeObservers,
    captureSubdivisionWidths,
    resetSubdivisionWidths
  } = useSubdivisionWidths();

  const getLongestString = useMemo(() =>
    getLongestStringLength(rowsPreload),
    [rowsPreload]
  );

  const calculatedColumnWidths = useMemo(() =>
    calculateColumnWidths(columns, rows, getLongestString, splitMode, attributeSeparationLines),
    [columns, rows, getLongestString, splitMode, attributeSeparationLines]
  );

  const startSubdivisionResize = (e, colIndex, type) => {
    const startX = e.clientX;
    const startWidth = subdivisionWidth[`${columns[colIndex]}-${type}`] ?? effectiveSubdivisionWidths[colIndex][type];

    const handleMouseMove = (e) => {
      const delta = e.clientX - startX;
      setSubdivisionWidth(prev => {
        const totalWidth = effectiveWidths[colIndex];
        const otherType = type === 'city' ? 'fao' : 'city';
        const newWidth = Math.min(Math.max(75, startWidth + delta), totalWidth - 75);
        return {
          ...prev,
          [`${columns[colIndex]}-${type}`]: newWidth,
          [`${columns[colIndex]}-${otherType}`]: totalWidth - newWidth
        }
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = '';
    };

    document.body.style.userSelect = 'none';
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const totalTableWidth = useMemo(
    () => calculatedColumnWidths.map((w, i) => customWidths[columns[i]] || w).reduce((sum, w) => sum + w, 0),
    [calculatedColumnWidths, customWidths, columns]
  );

  useEffect(() => {
    if (listRef.current) {
      listRef.current.forceUpdateGrid();
    }
  }, [customWidths, calculatedColumnWidths]);

  const startColumnResize = (e, colIndex) => {
    const startX = e.clientX;
    const startWidth = customWidths[columns[colIndex]] ?? calculatedColumnWidths[colIndex];
    const minWidth = colIndex === 0 ? 280 : 200;

    const handleMouseMove = (e) => {
      let newWidth = startWidth + (e.clientX - startX);
      newWidth = Math.max(minWidth, newWidth);
      if (colIndex === 0) {
        newWidth = Math.min(newWidth, 900);
      }
      setCustomWidths(prev => ({ ...prev, [columns[colIndex]]: newWidth }));
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
    };

    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const getRowName = (row) => {
    if (!row || row.length === 0) return 'unknown';
    const firstCell = row[0];
    if (Array.isArray(firstCell)) return firstCell[0];
    return firstCell;
  };

  const startRowResize = (e, listIndex) => {
    const item = rowsWithSeparation[listIndex];
    if (!item || item.type === 'category') return;

    const rowName = getRowName(item.row);
    const startHeight = rowHeights[rowName] || 40;
    const startY = e.clientY;

    const handleMouseMove = (e) => {
      const newHeight = Math.max(20, startHeight + (e.clientY - startY));
      setRowHeights(prev => {
        const updated = { ...prev, [rowName]: newHeight };
        setTimeout(() => {
          if (listRef.current) listRef.current.recomputeRowHeights(listIndex);
        }, 0);
        return updated;
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
    };

    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const effectiveWidths = useMemo(() => {
    if (!useCustomWidths) return calculatedColumnWidths;
    return calculatedColumnWidths.map((w, i) => customWidths[columns[i]] || w);
  }, [useCustomWidths, calculatedColumnWidths, customWidths, columns]);

  const effectiveSubdivisionWidths = useMemo(() => {
    return columns.map((col, i) => {
      const totalWidth = effectiveWidths[i];
      if (!useCustomWidths) {
        return { city: totalWidth / 2, fao: totalWidth / 2 };
      }
      return {
        city: subdivisionWidth[`${columns[i]}-city`] || totalWidth / 2,
        fao: subdivisionWidth[`${columns[i]}-fao`] || totalWidth / 2
      };
    });
  }, [useCustomWidths, columns, effectiveWidths, subdivisionWidth]);

  const getRowHeight = ({ index }) => {
    const item = rowsWithSeparation[index];
    if (!item) return 40;
    if (item.type === 'category') return 40;
    const rowName = getRowName(item.row);
    if (useCustomWidths) return rowHeights[rowName] || 40;
    return 40;
  };

  const rowsWithSeparation = useMemo(() =>
    createRowsWithSeparation(filteredRows, attributeSeparationLines),
    [filteredRows, attributeSeparationLines]
  );

  useEffect(() => {
    if (!cacheKey || dimensionsAppliedRef.current) return;

    const applyDimensions = (dim) => {
      setUseCustomWidths(dim.useCustomDimensions ?? true);
      if (dim.columnWidths) setCustomWidths(dim.columnWidths);
      if (dim.subdivisionWidths) setSubdivisionWidth(dim.subdivisionWidths);
      if (dim.rowHeights) setRowHeights(dim.rowHeights);

      setTimeout(() => {
        if (listRef?.current) {
          listRef.current.recomputeRowHeights();
          listRef.current.forceUpdateGrid();
        }
      }, 0);

      dimensionsAppliedRef.current = true;
    };

    const loadDimensions = async () => {
      try {
        const allDimensions = await DynamicTableService.getAllTableDimensions();
        if (Array.isArray(allDimensions)) {
          allDimensions.forEach(dim => {
            tableDimensionsStore[dim.tableName] = dim;
          });
        }

        const cached = tableDimensionsStore[cacheKey];
        if (cached) {
          applyDimensions(cached);
        }
      } catch (e) {
        console.error("Erro ao buscar dimensões da tabela:", e);
      } finally {
        setLoaded(true);
      }
    };

    loadDimensions();
  }, [cacheKey, setLoaded]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (e.button === 0) setMouseDown(true);
    };
    const handleMouseUp = (e) => {
      if (e.button === 0) setMouseDown(false);
    };

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const prevSentStateRef = useRef({
    columnWidths: {},
    subdivisionWidths: {},
    rowHeights: {},
    useCustomDimensions: true,
  });

  useEffect(() => {
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    if (mouseDown) return;

    const newState = {
      columnWidths: customWidths,
      subdivisionWidths: subdivisionWidth,
      rowHeights,
      useCustomDimensions: useCustomWidths
    };

    if (isEqual(prevSentStateRef.current, newState)) return;

    const handler = setTimeout(() => {
      DynamicTableService.saveOrUpdateTableDimensions(cacheKey, newState);
      prevSentStateRef.current = newState;
    }, 100);

    return () => clearTimeout(handler);

  }, [customWidths, subdivisionWidth, rowHeights, useCustomWidths, mouseDown, cacheKey]);

  useEffect(() => {
    setSubdivisionWidth(prev => {
      const updated = { ...prev };
      let changed = false;

      columns.forEach((_, colIndex) => {
        if (colIndex === 0) return;
        const totalWidth = effectiveWidths[colIndex];

        const cityKey = `${columns[colIndex]}-city`;
        const faoKey = `${columns[colIndex]}-fao`;

        let cityW = prev[cityKey] ?? totalWidth / 2;
        let faoW = prev[faoKey] ?? totalWidth / 2;

        const sum = cityW + faoW;
        if (Math.abs(sum - totalWidth) > 1) {
          const factor = totalWidth / sum;
          cityW = Math.max(75, cityW * factor);
          faoW = Math.max(75, faoW * factor);
        }

        if (cityW + faoW > totalWidth) {
          const excess = cityW + faoW - totalWidth;
          if (cityW > faoW) cityW -= excess;
          else faoW -= excess;
        }

        if (cityW !== prev[cityKey] || faoW !== prev[faoKey]) {
          changed = true;
        }

        updated[cityKey] = cityW;
        updated[faoKey] = faoW;
      });

      return changed ? updated : prev;
    });
  }, [effectiveWidths, columns]);

  const handleKeyDown = useTableNavigation(rows, splitMode, columns, calculatedColumnWidths, headerRef, scrollableRef);
  const { handleCellChange, handleCellBlur, handlePaste } = useCellInteractions(
    rows, setRows, setChanges, splitMode, fixedNetworkAdjustmentFactors,
    tempValues, updateTempValue, clearTempValue, title
  );

  const handleFocus = useCallback((input) => {
    const container = scrollableContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const inputRect = input.getBoundingClientRect();

    const offsetLeft = inputRect.left - containerRect.left;
    const offsetRight = inputRect.right - containerRect.right;
    const padding = 8;

    if (offsetLeft < 560) {
      container.scrollLeft -= offsetLeft;
    } else if (offsetRight > container.clientWidth) {
      container.scrollLeft += offsetRight - container.clientWidth + padding;
    }
  }, []);

  const updateRowsForSplitMode = useCallback((splitEnabled) => {

    const updatedRows = rows.map(row =>
      row.map(cell => {
        if (Array.isArray(cell)) return cell;
        return splitEnabled ? [cell, ''] : cell;
      })
    );

    if (JSON.stringify(rows) !== JSON.stringify(updatedRows)) {
      rowsPreloadRef.current = updatedRows;
      setRows(updatedRows);
      setChanges(false);
    }
  }, [rows, setRows, setChanges]);

  const handleSave = useCallback(() => {
    attributeStore[cacheKey] = [];
    setChanges(false);
    if (onSave) {
      onSave(rows);
    }
    if (wasFileUploaded) {
      setWasFileUploaded(false);
    }
  }, [cacheKey, onSave, rows, setChanges, wasFileUploaded, setWasFileUploaded]);

  const handleReset = useCallback(() => {
    LogsServices.postLog(`Ação: Dados resetados na etapa ${title}`);
    if (wasFileUploaded && originalDataBeforeUpload.current) {
      rowsPreloadRef.current = originalDataBeforeUpload.current;
      setRows(deepClone(originalDataBeforeUpload.current));
      attributeStore[cacheKey] = deepClone(originalDataBeforeUpload.current);
      setWasFileUploaded(false);
    } else {
      rowsPreloadRef.current = rowsPreload;
      setRows(deepClone(rowsPreload));
      attributeStore[cacheKey] = deepClone(rowsPreload);
    }
    setColumns(columnsPreload);
    setChanges(false);
  }, [rowsPreload, columnsPreload, cacheKey, setRows, setChanges, wasFileUploaded, setWasFileUploaded, originalDataBeforeUpload, title]);

  useEffect(() => {
    updateRowsForSplitMode(splitMode);
  }, [splitMode, updateRowsForSplitMode]);

  useEffect(() => {
    if (headerRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          const newHeight = entry.contentRect.height;
          setHeaderHeight(prevHeight => {
            if (Math.abs(newHeight - prevHeight) > 1) {
              return newHeight;
            }
            return prevHeight;
          });
        }
      });

      resizeObserver.observe(headerRef.current);

      const initialHeight = headerRef.current.offsetHeight;
      if (initialHeight > 0) {
        setHeaderHeight(prevHeight => {
          if (Math.abs(initialHeight - prevHeight) > 1) {
            return initialHeight;
          }
          return prevHeight;
        });
      }
      return () => resizeObserver.disconnect();
    }
  }, [headerHeight, stateGroupings, columns]);

  useEffect(() => {
    resetSubdivisionWidths();
  }, [columns, rows, attributeSeparationLines, resetSubdivisionWidths]);


  useEffect(() => {
    if (optional) {
      const fetchData = async () => {
        try {
          const apiResponse = await NavigationService.getNavigationSteps();
          setSteps(apiResponse);
        } catch (error) {
          console.error("Erro ao obter os passos de navegação:", error);
        }
      };
      fetchData();
    }
  }, [optional]);

  useEffect(() => {
    Object.keys(subdivisionRefs.current).forEach((key) => {
      const [colIndex, type] = key.split("-")
      if (!resizeObservers.current[colIndex] && type === "specialists") {
        const elA = subdivisionRefs.current[`${columns[colIndex]}-specialists`]
        const elB = subdivisionRefs.current[`${columns[colIndex]}-fao`]

        if (elA && elB){
          resizeObservers.current[colIndex] = captureSubdivisionWidths(Number(colIndex), elA, elB)
        }
      }
    })

    return (() => {
      Object.values(resizeObservers.current).forEach((disconnect) => {
        if (typeof disconnect === 'function') disconnect()
      })
      resizeObservers.current = {}
    })
  }, [columns, rows, captureSubdivisionWidths, resizeObservers, subdivisionRefs]);

  function renderRowItem ({ index, key, style }) {
    const item = rowsWithSeparation[index];
    if (item.type === 'category') {
      return (
        <div key={key} style={style}>
          <CategorySeparator
            separationLinesList={[item.category, item.specialists, item.fao]}
            columns={columns}
            calculateColumnWidths={effectiveWidths}
            subdivisionWidths={effectiveSubdivisionWidths}
            useCustomWidths={useCustomWidths}
            firstColumnWidth={calculatedColumnWidths[0]}
            splitMode={splitMode}
            subdivisionRefs={subdivisionRefs}
            resizeObservers={resizeObservers}
            captureSubdivisionWidths={captureSubdivisionWidths}
            handleFocus={handleFocus}
          />
        </div>
      );
    } else {
      return (
        <div key={key} style={style}>
          <TableRow
            title={title}
            row={item.row}
            originalIndex={item.originalIndex}
            calculateColumnWidths={effectiveWidths}
            firstColumnWidth={calculatedColumnWidths[0]}
            splitMode={splitMode}
            fixedNetworkAdjustmentFactors={fixedNetworkAdjustmentFactors}
            subdivisionWidths={effectiveSubdivisionWidths}
            attributeSeparationLines={attributeSeparationLines}
            hints={hints}
            tempValues={tempValues}
            handleCellChange={handleCellChange}
            handleCellBlur={handleCellBlur}
            handleKeyDown={handleKeyDown}
            handlePaste={handlePaste}
            handleFocus={handleFocus}
            rowHeights={rowHeights}
            rowName={getRowName(item.row)}
            startRowResize={(e, originalIndex) => {
              const listIndex = rowsWithSeparation.findIndex(item => item.originalIndex === originalIndex);
              startRowResize(e, listIndex);
            }}
            useCustomWidths={useCustomWidths}
          />
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col w-full h-full p-0 py-2 md:p-8 md:pt-4">
      <TableToolbar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        optionStatus={optionStatus}
        setOptionStatus={setOptionStatus}
        changes={changes}
        onSave={handleSave}
        onReset={handleReset}
        optional={optional}
        steps={steps}
        columnsPreload={columnsPreload}
        wasFileUploaded={wasFileUploaded}
      />

      <div className="relative flex-1 min-h-0">
        <ScrollHint
          className="flex flex-col w-full flex-1 min-h-0"
          hintIcon={<IoIosArrowBack size={20} />}
          scrollRef={scrollableRef}
          containerRef={scrollableContainerRef}
        >
          <div
            style={{
              maxHeight: `${maxHeight}px`,
              overflowX: "auto",
              overflowY: "visible",
              position: "relative"
            }}
            ref={scrollableRef}
          >
            <div style={{ width: totalTableWidth }}>
              {stateGroupings && (
                <div className="sticky top-0 z-40 bg-white shadow-md" ref={headerRef}>
                  <TableHeader
                    stateGroupings={stateGroupings}
                    columns={columns}
                    calculateColumnWidths={effectiveWidths}
                    tooltipVisible={tooltipVisible}
                    setTooltipVisible={setTooltipVisible}
                    headerRef={headerRef}
                    startColumnResize={startColumnResize}
                    effectiveSubdivisionWidths={effectiveSubdivisionWidths}
                    startSubdivisionResize={startSubdivisionResize}
                    cacheKey={cacheKey}
                    setCustomWidths={setCustomWidths}
                    setSubdivisionWidth={setSubdivisionWidth}
                    setRowHeights={setRowHeights}
                    useCustomWidths={useCustomWidths}
                    setUseCustomWidths={setUseCustomWidths}
                    listRef={listRef}
                  />
                </div>
              )}

              <div className="flex flex-col bg-[#F5F5F5] w-fit shadow-lg border h-auto">
                <List
                  ref={listRef}
                  width={totalTableWidth}
                  tabIndex={2}
                  height={Math.max(200, rowsWithSeparation.length * 40)}
                  rowCount={rowsWithSeparation.length}
                  rowHeight={getRowHeight}
                  style={{ overflowX: "visible", overflowY: "visible", zIndex: 30 }}
                  containerStyle={{ overflowX: "visible", overflowY: "visible" }}
                  rowRenderer={renderRowItem}
                />
              </div>
            </div>
          </div>
        </ScrollHint>
      </div>
    </div>
  );
};

export default DynamicTable;